import React, { useState, useRef } from 'react';
import {
	Grid,
	Button,
	TextField,
	FormControlLabel,
	Checkbox,
	Box,
	Typography,
	IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { schedulerService } from '../../../../core/services/schedulerService';
import styles from '../SchedulerSetting.module.scss';
import { createGeneralSettings, updateGeneralSettings } from 'core/API/guestConnect';

interface EditGeneralSettingsProps {
	settings: any;
	handleBackClick?: () => void;
	onSettingsUpdated?: () => void;
}

const WORK_WEEK_START_OPTIONS = [
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
	'Sunday',
];

const EditGeneralSettings: React.FC<EditGeneralSettingsProps> = ({
	settings,
	handleBackClick,
	onSettingsUpdated,
}) => {
	const [settingsBuffer, setSettingsBuffer] = useState(() => {
		// Get workplace from localStorage if not provided in settings
		const getWorkplace = () => {
			if (settings?.workplace) {
				return settings.workplace;
			}
			const workplaceId = localStorage.getItem('workplace-id');
			return workplaceId ? `/api/workplaces/${workplaceId}` : null;
		};

		return {
			workplace: getWorkplace(),
			logoPath: settings?.logoPath || '',
			disclaimerText: settings?.disclaimerText || '',
			allowMultipleSubmissionsPerDay: settings?.allowMultipleSubmissionsPerDay || false,
		};
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [logoFile, setLogoFile] = useState<File | null>(null);
	const [logoPreview, setLogoPreview] = useState<string | null>(null);
	const [isUploadingLogo, setIsUploadingLogo] = useState(false);
	const [isImageChanged, setIsImageChanged] = useState(false); // Track if image has been changed
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	// Get logo URL helper function
	const getLogoUrl = (logoPath: string): string => {
		if (!logoPath) return '';
		// If it's already a full URL, return as is
		if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
			return logoPath;
		}
		// Use base URL for preview
		return `${process.env.REACT_APP_IMAGE_URL}/guest_connect_logo/${logoPath}`;
	};

	// Initialize preview from existing logo only on mount or when settings change but no new file is selected
	React.useEffect(() => {
		// Only set preview from existing logo if no new file has been selected
		if (settings?.logoPath && !logoFile) {
			setLogoPreview(getLogoUrl(settings.logoPath));
		}
	}, [settings?.logoPath, logoFile]);

	// Cleanup blob URLs on unmount
	React.useEffect(() => {
		return () => {
			if (logoPreview && logoPreview.startsWith('blob:')) {
				URL.revokeObjectURL(logoPreview);
			}
		};
	}, [logoPreview]);

	const validateSettings = (): boolean => {
		// Validate logo is mandatory
		// Check if there's a new file selected OR existing logo path
		const hasLogo = logoFile || (settingsBuffer.logoPath && settingsBuffer.logoPath.trim() !== '');
		if (!hasLogo) {
			setError('Logo is required');
			return false;
		}

		// Clear error if logo is present
		if (hasLogo && error && error.includes('Logo is required')) {
			setError(null);
		}

		return true;
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) {
			setLogoFile(null);
			// Only revert to old preview if no file is selected
			setLogoPreview(settings.logoPath ? getLogoUrl(settings.logoPath) : null);
			return;
		}

		const MAX_SIZE_MB = 5;
		if (file.size > MAX_SIZE_MB * 1024 * 1024) {
			alert(`File size should not exceed ${MAX_SIZE_MB}MB.`);
			e.target.value = '';
			setLogoFile(null);
			// Revert to old preview on error
			setLogoPreview(settings.logoPath ? getLogoUrl(settings.logoPath) : null);
			return;
		}

		// Clear error when file is selected
		if (error && error.includes('Logo is required')) {
			setError(null);
		}

		// Revoke previous preview URL to prevent memory leaks
		if (logoPreview && logoPreview.startsWith('blob:')) {
			URL.revokeObjectURL(logoPreview);
		}

		// Create preview for new file
		const previewUrl = URL.createObjectURL(file);
		setLogoPreview(previewUrl);
		setLogoFile(file);
		setIsImageChanged(true); // Mark image as changed when new file is selected
	};

	console.log("Selected file:", logoFile);


	const handleRemoveLogo = () => {
		// Revoke preview URL if it's a blob URL
		if (logoPreview && logoPreview.startsWith('blob:')) {
			URL.revokeObjectURL(logoPreview);
		}

		setLogoFile(null);
		setLogoPreview(null);
		setIsImageChanged(true); // Mark image as changed when removing
		// Clear logoPath from buffer when removing
		setSettingsBuffer({
			...settingsBuffer,
			logoPath: '',
		});
		// Reset file input
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (!validateSettings()) {
			return;
		}

		try {
			setIsSubmitting(true);
			setIsUploadingLogo(logoFile ? true : false);

			const settingsParams: any = {
				workplace: settingsBuffer.workplace,
				disclaimerText: settingsBuffer.disclaimerText,
				allowMultipleSubmissionsPerDay: settingsBuffer.allowMultipleSubmissionsPerDay,
			};

			// Determine which API to use based on whether image was changed
			if (isImageChanged) {
				// Image was changed (uploaded or removed)
				if (logoFile) {
					// New file uploaded - use multipart API
					settingsParams.logoFile = logoFile;
				} else {
					// Image was removed but marked as changed - this should be caught by validation
					throw new Error('Logo is required');
				}
				// Check if settings already exist (has ID) - use PUT for update, POST for create
				if (settings?.id) {
					await updateGeneralSettings(settings.id, settingsParams);
				} else {
					await createGeneralSettings(settingsParams);
				}
			} else {
				// Image was NOT changed - use JSON API with logoPath and logoFile as null
				settingsParams.logoPath = null;
				settingsParams.logoFile = null;
				
				// Check if settings already exist (has ID) - use PUT for update, POST for create
				if (settings?.id) {
					await updateGeneralSettings(settings.id, settingsParams);
				} else {
					await createGeneralSettings(settingsParams);
				}
			}

			// Clean up preview URL after successful upload
			if (logoPreview && logoPreview.startsWith('blob:')) {
				URL.revokeObjectURL(logoPreview);
			}

			// Reset file input and state after successful submission
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
			setLogoFile(null);
			setLogoPreview(null);
			setIsImageChanged(false); // Reset image changed flag after successful submission

			onSettingsUpdated?.();
			setTimeout(() => {
				handleBackClick?.();
			}, 500);
		} catch (err: any) {
			setError(err?.message || 'Failed to update settings');
			setIsSubmitting(false);
			setIsUploadingLogo(false);
		} finally {
			setIsSubmitting(false);
			setIsUploadingLogo(false);
		}
	};

	return (
		<div className={styles.editGeneralSettings}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={handleBackClick} variant="contained">
						Back
					</Button>
					<Button
						className={styles.submitButton}
						onClick={handleSubmit}
						disabled={isSubmitting}
						variant="contained"
					>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>
						{settings?.id ? 'Edit General Settings' : 'Create General Settings'}
					</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{/* Logo Upload Section */}
				<Box sx={{ marginBottom: 3 }}>
					<Typography variant="body1" sx={{ marginBottom: 2, fontWeight: 500 }}>
						Logo <span style={{ color: 'red' }}>*</span>
					</Typography>				
				<Typography variant="body2" sx={{ marginBottom: 1.5, color: 'text.secondary', fontSize: '0.875rem' }}>
					Supported formats: JPG, PNG, GIF, SVG, WEBP • Maximum file size: 5MB
				</Typography>
					{/* File Input - Visible */}
					<TextField
						key={logoFile ? 'file-selected' : 'no-file'}
						name="logoPath"
						type="file"
						inputRef={fileInputRef}
						onChange={handleFileChange}
						className={styles.input}
						margin="normal"
						fullWidth
						required
						inputProps={{ accept: 'image/*' }}
						disabled={isUploadingLogo || isSubmitting}
						error={error && error.includes('Logo is required')}
						helperText={error && error.includes('Logo is required') ? error : ''}
					/>

					{/* Logo Preview - Below Input - Square Box */}
					{logoPreview && (
						<Box sx={{ marginTop: 2, position: 'relative', display: 'inline-block' }}>
							<Box
								sx={{
									width: 200,
									height: 200,
									border: '2px solid #e0e0e0',
									borderRadius: 1,
									overflow: 'hidden',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									backgroundColor: '#f5f5f5',
								}}
							>
								<img
									src={logoPreview}
									alt="Logo Preview"
									style={{
										width: '100%',
										height: '100%',
										objectFit: 'contain',
									}}
								/>
							</Box>
							{/* Cross Icon - Top Right Corner */}
							<IconButton
								onClick={handleRemoveLogo}
								sx={{
									position: 'absolute',
									top: -8,
									right: -8,
									backgroundColor: 'white',
									border: '1px solid #e0e0e0',
									width: 32,
									height: 32,
									'&:hover': {
										backgroundColor: '#f5f5f5',
									},
								}}
								disabled={isUploadingLogo || isSubmitting}
							>
								<CloseIcon sx={{ fontSize: 20, color: '#666' }} />
							</IconButton>
							{logoFile && (
								<Typography variant="body2" color="textSecondary" sx={{ marginTop: 1 }}>
									{logoFile.name}
								</Typography>
							)}
						</Box>
					)}
				</Box>

				<TextField
					label="Disclaimer Text"
					name="disclaimerText"
					type="text"
					value={settingsBuffer.disclaimerText}
					onChange={(e) =>
						setSettingsBuffer({
							...settingsBuffer,
							disclaimerText: String(e.target.value),
						})
					}
					placeholder="Enter text"
					className={styles.input}
					margin="normal"
					fullWidth
					required
					multiline
					rows={4} // <-- Adjust height
				/>

				<FormControlLabel
					control={
						<Checkbox
							checked={Boolean(settingsBuffer.allowMultipleSubmissionsPerDay)}
							onChange={(e) =>
								setSettingsBuffer({
									...settingsBuffer,
									allowMultipleSubmissionsPerDay: e.target.checked ? 1 : 0, // or true/false depending on your DB
								})
							}
						/>
					}
					label="Customer can submit more than one survey per day."
				/>
			</form>
		</div>
	);
};

export default EditGeneralSettings;
